import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return (
    <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
      <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
      <h2 className="font-display text-xl font-bold">Administrator Only</h2>
      <p className="text-muted-foreground text-sm mt-2">Only Admins can manage CRM users.</p>
    </div>
  );
  return children;
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode, data}
  const [pwdFor, setPwdFor] = useState(null);
  const [pwd, setPwd] = useState("");

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const roleName = (k) => roles.find((r) => r.key === k)?.name || k;

  const openCreate = () => setDialog({ mode: "create", data: { name: "", email: "", mobile: "", role: "supervisor", password: "", is_active: true } });
  const openEdit = (u) => setDialog({ mode: "edit", data: { ...u } });

  const save = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === "create") {
        if (!d.name || !d.email || !d.password) { toast.error("Name, email and password are required"); return; }
        await api.post("/users", d);
        toast.success("User created");
      } else {
        await api.put(`/users/${d.id}`, { name: d.name, mobile: d.mobile, role: d.role, is_active: d.is_active });
        toast.success("User updated");
      }
      setDialog(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPwd = async () => {
    try { await api.post(`/users/${pwdFor.id}/reset-password`, { password: pwd }); toast.success("Password reset"); setPwdFor(null); setPwd(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove access for ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminOnly>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline text-muted-foreground mb-1">Access Control</div>
            <h1 className="font-display text-2xl font-bold tracking-tight">CRM Users</h1>
            <p className="text-sm text-muted-foreground">People who can log in. Separate from Staff &amp; Labour records.</p>
          </div>
          <Button className="gap-1.5" onClick={openCreate} data-testid="add-user-btn"><Icons.UserPlus className="h-4 w-4" />New User</Button>
        </div>

        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                {["Name", "Email", "Role", "Mobile", "Status", "Actions"].map((h) => <TableHead key={h} className="overline text-muted-foreground">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><Badge variant="secondary">{roleName(u.role)}</Badge></TableCell>
                  <TableCell>{u.mobile || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={u.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400"}>
                      {u.is_active ? "Active" : "Deactivated"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`user-menu-${u.id}`}><Icons.MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Icons.Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(u)} disabled={u.id === user.id} data-testid={`toggle-active-${u.id}`}><Icons.Power className="h-4 w-4 mr-2" />{u.is_active ? "Deactivate" : "Activate"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPwdFor(u)} data-testid={`open-reset-pwd-${u.id}`}><Icons.KeyRound className="h-4 w-4 mr-2" />Reset Password</DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => remove(u)} disabled={u.id === user.id} data-testid={`remove-user-${u.id}`}><Icons.Trash2 className="h-4 w-4 mr-2" />Remove</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">{dialog?.mode === "create" ? "New CRM User" : "Edit User"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="overline text-muted-foreground">Full Name</Label>
                <Input value={dialog.data.name} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })} data-testid="user-name" /></div>
              {dialog.mode === "create" && (
                <div className="space-y-1.5"><Label className="overline text-muted-foreground">Email</Label>
                  <Input type="email" value={dialog.data.email} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, email: e.target.value } })} data-testid="user-email" /></div>
              )}
              <div className="space-y-1.5"><Label className="overline text-muted-foreground">Mobile</Label>
                <Input value={dialog.data.mobile} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, mobile: e.target.value } })} data-testid="user-mobile" /></div>
              <div className="space-y-1.5"><Label className="overline text-muted-foreground">Role</Label>
                <Select value={dialog.data.role} onValueChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, role: v } })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                </Select></div>
              {dialog.mode === "create" && (
                <div className="space-y-1.5"><Label className="overline text-muted-foreground">Temporary Password</Label>
                  <Input type="text" value={dialog.data.password} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, password: e.target.value } })} data-testid="user-password" /></div>
              )}
              <div className="flex items-center justify-between pt-1">
                <Label className="text-sm">Account Active</Label>
                <Switch checked={dialog.data.is_active} onCheckedChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, is_active: v } })} data-testid="user-active" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwdFor} onOpenChange={(o) => !o && setPwdFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reset Password — {pwdFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">New Password</Label>
            <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdFor(null)}>Cancel</Button>
            <Button onClick={resetPwd} disabled={!pwd} data-testid="reset-pwd-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminOnly>
  );
}
